import React from 'react';
import { Link } from 'components';
import Badges from 'components/badges';
import SocialIcons from 'components/social-icons';
import { styled } from 'themes';
import { device } from 'themes/media-queries';
import { useTranslation } from 'react-i18next';
import { ROUTES } from 'navigation/route-map';

const FooterSection = () => {
  const [t] = useTranslation();
  return (
    <Section>
      <Wrapper>
        <Content>
          <h3>{t('section--footer.heading')}</h3>
          <p fontSize={2} color="white">
            {t('section--footer.subheading-1')}
          </p>

          <Badges my={3} />
          <p>{t('section--footer.subheading-2')}</p>
          <SocialIcons my={3} />
        </Content>
        <Contact>
          <h4>{t('section--footer.company-name')}</h4>
          <p>{t('section--footer.address')}</p>
          <p>{t('section--footer.org-num')}</p>
          <p>
            <span>{t('section--footer.emergency-phone-label')}</span>
            <Link
              to={'tel:0851519000'}>
              {t('section--footer.emergency-phone-num')}
            </Link>
          </p>
          <Link
            to={'https://support.yepstr.com'}>
            {t('section--footer.support')}
          </Link>
        </Contact>
      </Wrapper>
      <Navigation>
        <div>
          <h6>{t('section--footer.navigation.title-1')}</h6>
          <ul>
            <li>
              <Link to={ROUTES.HOME}>{t('section--footer.navigation.home')}</Link>
            </li>
            <li>
              <Link to={ROUTES.YEP}>
                {t('section--footer.navigation.yep')}
              </Link>
            </li>
            <li>
              <Link to="https://support.yepstr.com" target="_blank">
                {t('section--footer.navigation.support')}
              </Link>
            </li>
            <li>
              <Link to={ROUTES.ABOUT}>
                {t('section--footer.navigation.about')}
              </Link>
            </li>
            <li>
              <Link to="https://www.yepstr.com/blog" target="_blank">
                {t('section--footer.navigation.blog')}
              </Link>
            </li>
            <li>
              <Link to={ROUTES.DOWNLOAD}>
                {t('section--footer.navigation.download')}
              </Link>
            </li>\
          </ul>
        </div>
        <div>
          <h6>{t('section--footer.navigation.title-2')}</h6>
          <ul>
            <li>
              <Link to={ROUTES.BABYSITTING}>
                {t('section--footer.navigation.babysitting')}
              </Link>
            </li>
            <li>
              <Link to={ROUTES.HOMEWORK}>
                {t('section--footer.navigation.homework')}
              </Link>
            </li>
            <li>
              <Link to={ROUTES.GARDENING}>
                {t('section--footer.navigation.gardening')}
              </Link>
            </li>
            <li>
              <Link to={ROUTES.EVENT}>
                {t('section--footer.navigation.event')}
              </Link>
            </li>
            <li>
              <Link to={ROUTES.DOGMINDING}>
                {t('section--footer.navigation.dog-minding')}
              </Link>
            </li>
            <li>
              <Link to="https://business.yepstr.com" target="_blank">
                {t('section--footer.navigation.company')}
              </Link>
            </li>
          </ul>
        </div>
      </Navigation>
    </Section>
  );
};

const Section = styled.section`
  grid-column: full-start / full-end;
  background-color: ${props => props.theme.colors.almostBlack};
  margin-top: -10rem;
  padding-top: 6rem;
  padding-bottom: 6rem;

  display: grid;
  grid-template-columns:
    [full-start] minmax(6rem, 1fr) [semi-full-start] minmax(6rem, 1fr) [center-start]
    repeat(8, [col-start] minmax(min-content, 18rem) [col-end])
    [center-end] minmax(6rem, 1fr) [semi-full-end] minmax(6rem, 1fr) [full-end];

  @media ${device.laptop} {
    grid-template-columns:
      [full-start] minmax(3rem, 1fr) [semi-full-start] minmax(3rem, 1fr) [center-start]
      repeat(8, [col-start] minmax(min-content, 18rem) [col-end])
      [center-end] minmax(3rem, 1fr) [semi-full-end] minmax(3rem, 1fr) [full-end];
  }

  @media ${device.mobileL} {
    padding-top: 2rem;
    padding-bottom: 2rem;

    grid-template-columns:
      [full-start] minmax(1rem, 1fr) [semi-full-start] minmax(1rem, 1fr) [center-start]
      repeat(8, [col-start] minmax(min-content, 18rem) [col-end])
      [center-end] minmax(1rem, 1fr) [semi-full-end] minmax(1rem, 1fr) [full-end];
  }
`;

const Wrapper = styled.div`
  grid-column: center-start / center-end;
  display: flex;
  justify-content: space-between;
  margin-bottom: 2rem;

  @media ${device.laptop} {
    flex-direction: column;
  }
`;

const Content = styled.div`
  & > h3 {
    margin-bottom: 2rem;
  }

  & > h3,
  & > p {
    color: white;
  }

  @media ${device.laptop} {
    margin-bottom: 2rem;
  }
`;

const Contact = styled.div`
  color: white;
  text-align: right;

  & span {
    margin-right: 1rem;
  }

  & a,
  & a:visited,
  & a:link {
    text-decoration: none;
    color: ${({ theme }) => theme.colors.orange};
    font-weight: bold;
    font-size: 2rem;
  }

  @media ${device.laptop} {
    text-align: left;
  }
`;

const Navigation = styled.nav`
  grid-column: center-start / center-end;
  display: grid;
  grid-template-columns: 1fr;
  grid-column-gap: 5rem;

  ul {
    list-style: none;
    display: flex;
    flex-wrap: wrap;
    margin-bottom: 1.5rem;

    li:not(:last-child) {
      margin-right: 2rem;
    }
  }

  h6 {
    color: white;
    font-size: 1.7rem;
    font-weight: 400;
  }

  & a:link,
  & a:visited {
    text-decoration: none;
    font-size: 2rem;
    color: ${({ theme }) => theme.colors.orange};
    font-weight: 700;
  }
`;

export default FooterSection;
